import { useMutation } from '@apollo/client';
import {
  DynamicSingleSelect,
  Flexbox,
  SmallSearch,
  Button
} from '@ibinder/common-frontend';
import { plus } from '@ibinder/common-icons';
import { capitalize } from '@ibinder/common-utils';
import styled from 'styled-components';
import React from 'react';
import { GET_GLOBAL_INSPECTION_CATEGORIES } from '../../api/queries/queries_local';
import { CREATE_INSPECTION_CATEGORY } from '../../api/mutations/inspectionTemplate.query';
import { iBinderHeaderEnum, Region } from '@ibinder/ui-common-models';

const ResultWrapper = styled(Flexbox)`
  padding: 0.75rem;

  &:hover {
    background: ${(p) => p.theme.colors.primary10};
  }

  &:focus {
    background: ${(p) => p.theme.colors.primary20};
    outline: none;
  }
`;

const StyledButton = styled(Button)`
  margin-left: auto;
  width: auto;
`;

const FooterWrapper = styled(Flexbox)`
  & > div {
    padding: 0.5rem;
  }
`;

const UpperFooter = styled(Flexbox)`
  border-top: 0.0625rem solid ${(p) => p.theme.colors.discrete20};

  & > div:nth-child(2) {
    padding-top: 0.75rem;

    p:nth-child(1) {
      font-weight: ${(p) => p.theme.fontStyles.smallBoldFont.fontWeight};
    }

    p:nth-child(2) {
      color: ${(p) => p.theme.colors.discrete50};
    }
  }

  &:hover {
    background: ${(p) => p.theme.colors.primary10};
  }
`;

const LowerFooter = styled(Flexbox)`
  background: ${(p) => p.theme.colors.discrete10};
  cursor: auto;
`;

const InspectionCategoryDropdown = ({
  labels,
  activeKey,
  data,
  onSelect,
  userId,
  market
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const filterCategories = (data) => {
    return data.filter((category) =>
      category.Value.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const [createInspectionCategory] = useMutation(CREATE_INSPECTION_CATEGORY, {
    context: {
      headers: {
        [iBinderHeaderEnum.REGION]: Region.International
      }
    }
  });

  const onCreateNewCategory = (closeParent) => () => {
    createInspectionCategory({
      variables: {
        input: {
          name: searchTerm,
          market: market
        }
      },
      refetchQueries: [GET_GLOBAL_INSPECTION_CATEGORIES],
      onCompleted: ({ InspectionCategory }) => {
        onSelect(InspectionCategory.id);
        closeParent();
      }
    });
  };

  const renderResults =
    (searched, { handleSelect }) =>
    () => {
      return filterCategories(data).map((result, i) => (
        <ResultWrapper
          {...{
            key: i,
            onClick: () => handleSelect(result),
            alignItems: 'center'
          }}
        >
          <div>{result.Value}</div>
        </ResultWrapper>
      ));
    };

  const renderFooter = ({ closeParent, labels }) => {
    return (
      <FooterWrapper flexDirection={'column'}>
        {searchTerm && (
          <UpperFooter {...{ onClick: onCreateNewCategory(closeParent) }}>
            <Flexbox flexDirection={'column'}>{plus}</Flexbox>
            <Flexbox flexDirection={'column'}>
              <p>{searchTerm}</p>
              <p>{labels.createNewCategory}</p>
            </Flexbox>
          </UpperFooter>
        )}

        <LowerFooter>
          <StyledButton
            {...{
              btnType: 'regular',
              btnStyle: 'secondary',
              type: 'submit',
              onClick: closeParent,
              label: capitalize(labels.footerRightBtn)
            }}
          />
        </LowerFooter>
      </FooterWrapper>
    );
  };

  return (
    <DynamicSingleSelect
      {...{
        label: labels.label,
        dropdownText: labels.select,
        data,
        idKey: 'Key',
        valueKey: 'Value',
        activeKey,
        changeActiveItem: (e) => onSelect(e),
        onToggle: () => setSearchTerm(''),
        required: true
      }}
    >
      <SmallSearch
        {...{
          renderResults,
          renderFooter,
          loading: false,
          labels,
          setSearchTerm: (value) => {
            setSearchTerm(capitalize(value));
          },
          searchTerm
        }}
      />
    </DynamicSingleSelect>
  );
};

export default InspectionCategoryDropdown;
